import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Save, RotateCcw, FileText } from "lucide-react";
import { toast } from "sonner";
import { DEFAULT_CONTENT } from "./content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// [key, label, multiline]
const SECTIONS = [
  {
    title: "Hero",
    hint: "The first thing visitors see at the top of the page.",
    fields: [
      ["hero_eyebrow", "Eyebrow (small label)", false],
      ["hero_title", "Headline (one line per row)", true],
      ["hero_tagline", "Italic tagline", false],
      ["hero_subtitle", "Intro paragraph", true],
      ["hero_cta_primary", "Primary button label", false],
      ["hero_cta_secondary", "Secondary button label", false],
    ],
  },
  {
    title: "Services",
    hint: "The 'What we do' section heading.",
    fields: [
      ["services_eyebrow", "Eyebrow", false],
      ["services_title", "Heading", true],
      ["services_subtitle", "Intro paragraph", true],
    ],
  },
  {
    title: "Approach / Manifesto",
    hint: "The 'Our approach' section heading.",
    fields: [
      ["approach_eyebrow", "Eyebrow", false],
      ["approach_title", "Heading", true],
      ["approach_subtitle", "Intro paragraph", true],
    ],
  },
  {
    title: "Team",
    hint: "The 'Meet the team' section heading.",
    fields: [
      ["team_eyebrow", "Eyebrow", false],
      ["team_title", "Heading", true],
    ],
  },
  {
    title: "Testimonials",
    hint: "The client proof section heading.",
    fields: [
      ["testimonials_eyebrow", "Eyebrow", false],
      ["testimonials_title", "Heading", true],
    ],
  },
  {
    title: "Careers",
    hint: "The careers section heading.",
    fields: [
      ["careers_eyebrow", "Eyebrow", false],
      ["careers_title", "Heading", true],
      ["careers_subtitle", "Intro paragraph", true],
    ],
  },
  {
    title: "Contact",
    hint: "The contact section heading.",
    fields: [
      ["contact_eyebrow", "Eyebrow", false],
      ["contact_title", "Heading", true],
      ["contact_subtitle", "Intro paragraph", true],
    ],
  },
];

export const ContentTab = ({ auth }) => {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`)
      .then((r) => setContent({ ...DEFAULT_CONTENT, ...r.data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/content`, content, auth());
      setContent({ ...DEFAULT_CONTENT, ...r.data });
      toast.success("Section text saved — it's now live on your website.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save content.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    try {
      const r = await axios.post(`${API}/admin/content/reset`, {}, auth());
      setContent({ ...DEFAULT_CONTENT, ...r.data });
      toast.success("Section text reset to default.");
    } catch (e) {
      toast.error("Couldn't reset content.");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return <div className="py-20 text-center text-foreground/50"><Loader2 className="mx-auto animate-spin" size={26} /></div>;
  }

  return (
    <div data-testid="content-tab">
      <div className="flex items-center gap-3 mb-6">
        <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary/10 text-primary"><FileText size={20} /></span>
        <div>
          <h2 className="font-heading text-2xl font-medium">Section text</h2>
          <p className="text-sm text-foreground/55">Edit the wording of your homepage sections.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="reset-content-btn"
            onClick={reset}
            disabled={resetting}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground/70 hover:text-primary hover:border-primary transition-colors disabled:opacity-60"
          >
            {resetting ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} Reset to default
          </button>
          <button
            data-testid="save-content-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-6 py-2.5 text-sm font-medium hover:bg-[#0d5f57] transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save & publish
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {SECTIONS.map((sec) => (
          <div key={sec.title} className="bg-card border border-border rounded-3xl p-6">
            <div className="mb-4">
              <h3 className="font-heading text-lg font-medium">{sec.title}</h3>
              <p className="text-xs text-foreground/45">{sec.hint}</p>
            </div>
            <div className="space-y-4">
              {sec.fields.map(([key, label, multiline]) => (
                <div key={key}>
                  <label className="text-xs font-medium text-foreground/60 mb-2 block">{label}</label>
                  {multiline ? (
                    <textarea
                      data-testid={`content-${key}`}
                      value={content[key] || ""}
                      onChange={(e) => set(key, e.target.value)}
                      rows={key.endsWith("subtitle") ? 3 : 2}
                      className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-primary transition-colors resize-none"
                    />
                  ) : (
                    <input
                      data-testid={`content-${key}`}
                      value={content[key] || ""}
                      onChange={(e) => set(key, e.target.value)}
                      className="w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-primary transition-colors"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
